/**
 * Speech Recognition Service
 *
 * Uses the built-in Web Speech API for on-device speech-to-text.
 * Falls back to Deepgram for better accuracy when API key is available.
 *
 * Benefits of Web Speech API:
 * - Free (no API costs)
 * - Private (audio stays on device)
 * - Lower latency (no network roundtrip)
 * - Works on Chrome, Safari, Edge, and mobile browsers
 */

export interface TranscriptionResult {
  text: string
  isFinal: boolean
  confidence: number
  timestamp: number
}

export interface SpeechRecognitionOptions {
  language?: string
  continuous?: boolean
  interimResults?: boolean
  onResult?: (result: TranscriptionResult) => void
  onError?: (error: string) => void
  onStart?: () => void
  onEnd?: () => void
}

// Type definitions for Web Speech API
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList
  resultIndex: number
}

interface SpeechRecognitionResultList {
  length: number
  item(index: number): SpeechRecognitionResult
  [index: number]: SpeechRecognitionResult
}

interface SpeechRecognitionResult {
  length: number
  item(index: number): SpeechRecognitionAlternative
  [index: number]: SpeechRecognitionAlternative
  isFinal: boolean
}

interface SpeechRecognitionAlternative {
  transcript: string
  confidence: number
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string
  message?: string
}

// Global type augmentation
declare global {
  interface Window {
    SpeechRecognition: new () => SpeechRecognition
    webkitSpeechRecognition: new () => SpeechRecognition
  }
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean
  interimResults: boolean
  lang: string
  maxAlternatives: number
  start(): void
  stop(): void
  abort(): void
  onresult: ((event: SpeechRecognitionEvent) => void) | null
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null
  onstart: (() => void) | null
  onend: (() => void) | null
  onspeechstart: (() => void) | null
  onspeechend: (() => void) | null
}

/**
 * Check if Web Speech API is available
 */
export function isSpeechRecognitionAvailable(): boolean {
  return typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)
}

/**
 * Create a speech recognition instance
 */
export function createSpeechRecognition(): SpeechRecognition | null {
  if (typeof window === 'undefined') return null

  const SpeechRecognitionClass = window.SpeechRecognition || window.webkitSpeechRecognition
  if (!SpeechRecognitionClass) return null

  return new SpeechRecognitionClass()
}

/**
 * Speech Recognition Manager
 *
 * Handles continuous speech recognition with automatic restart on end.
 */
export class SpeechRecognitionManager {
  private recognition: SpeechRecognition | null = null
  private isRunning = false
  private shouldRestart = false
  private options: SpeechRecognitionOptions
  private transcripts: TranscriptionResult[] = []

  constructor(options: SpeechRecognitionOptions = {}) {
    this.options = {
      language: 'en-US',
      continuous: true,
      interimResults: true,
      ...options,
    }
  }

  /**
   * Start speech recognition
   */
  start(): boolean {
    if (this.isRunning) {
      console.log('[SpeechRecognition] Already running')
      return true
    }

    if (!isSpeechRecognitionAvailable()) {
      console.error('[SpeechRecognition] Not available in this browser')
      this.options.onError?.('Speech recognition not available in this browser')
      return false
    }

    this.recognition = createSpeechRecognition()
    if (!this.recognition) {
      this.options.onError?.('Failed to create speech recognition')
      return false
    }

    // Configure
    this.recognition.continuous = this.options.continuous ?? true
    this.recognition.interimResults = this.options.interimResults ?? true
    this.recognition.lang = this.options.language ?? 'en-US'
    this.recognition.maxAlternatives = 1

    // Event handlers
    this.recognition.onstart = () => {
      console.log('[SpeechRecognition] Started')
      this.isRunning = true
      this.shouldRestart = true
      this.options.onStart?.()
    }

    this.recognition.onend = () => {
      console.log('[SpeechRecognition] Ended')
      this.isRunning = false
      this.options.onEnd?.()

      // Auto-restart for continuous recognition
      if (this.shouldRestart) {
        console.log('[SpeechRecognition] Auto-restarting...')
        setTimeout(() => {
          if (this.shouldRestart) {
            this.start()
          }
        }, 100)
      }
    }

    this.recognition.onresult = (event: SpeechRecognitionEvent) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        const transcript = result[0].transcript
        const confidence = result[0].confidence
        const isFinal = result.isFinal

        const transcriptionResult: TranscriptionResult = {
          text: transcript,
          isFinal,
          confidence: confidence || 0.9, // Some browsers don't provide confidence
          timestamp: Date.now(),
        }

        if (isFinal) {
          this.transcripts.push(transcriptionResult)
          console.log('[SpeechRecognition] Final:', transcript)
        } else {
          console.log('[SpeechRecognition] Interim:', transcript)
        }

        this.options.onResult?.(transcriptionResult)
      }
    }

    this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error('[SpeechRecognition] Error:', event.error)

      // Some errors are recoverable
      if (event.error === 'no-speech' || event.error === 'aborted') {
        // These are normal, will auto-restart
        return
      }

      this.options.onError?.(event.error)

      // Stop on fatal errors
      if (event.error === 'not-allowed') {
        this.shouldRestart = false
      }
    }

    try {
      this.recognition.start()
      return true
    } catch (err) {
      console.error('[SpeechRecognition] Failed to start:', err)
      this.options.onError?.('Failed to start speech recognition')
      return false
    }
  }

  /**
   * Stop speech recognition
   */
  stop(): void {
    this.shouldRestart = false
    this.isRunning = false

    if (this.recognition) {
      try {
        this.recognition.stop()
      } catch (err) {
        // Ignore errors when stopping
      }
      this.recognition = null
    }

    console.log('[SpeechRecognition] Stopped')
  }

  /**
   * Get all final transcripts
   */
  getTranscripts(): TranscriptionResult[] {
    return [...this.transcripts]
  }

  /**
   * Get full transcript as text
   */
  getFullTranscript(): string {
    return this.transcripts.map(t => t.text).join(' ')
  }

  /**
   * Clear transcripts
   */
  clearTranscripts(): void {
    this.transcripts = []
  }

  /**
   * Check if running
   */
  isActive(): boolean {
    return this.isRunning
  }
}

/**
 * Deepgram Real-time Transcription (for higher accuracy)
 *
 * Uses WebSocket to stream audio to Deepgram for transcription.
 * Requires DEEPGRAM_API_KEY environment variable.
 */
export class DeepgramTranscription {
  private ws: WebSocket | null = null
  private mediaRecorder: MediaRecorder | null = null
  private isRunning = false
  private options: SpeechRecognitionOptions
  private apiKey: string

  constructor(apiKey: string, options: SpeechRecognitionOptions = {}) {
    this.apiKey = apiKey
    this.options = {
      language: 'en-US',
      ...options,
    }
  }

  /**
   * Start Deepgram transcription from a MediaStream
   */
  async start(stream: MediaStream): Promise<boolean> {
    if (this.isRunning) return true

    try {
      // Connect to Deepgram WebSocket
      const wsUrl = `wss://api.deepgram.com/v1/listen?encoding=linear16&sample_rate=16000&channels=1&language=${this.options.language}&model=nova-2&smart_format=true&punctuate=true`

      this.ws = new WebSocket(wsUrl, ['token', this.apiKey])

      this.ws.onopen = () => {
        console.log('[Deepgram] Connected')
        this.isRunning = true
        this.options.onStart?.()

        // Start recording and sending audio
        this.startRecording(stream)
      }

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          if (data.type === 'Results') {
            const transcript = data.channel?.alternatives?.[0]?.transcript || ''
            const isFinal = data.is_final || false
            const confidence = data.channel?.alternatives?.[0]?.confidence || 0.9

            if (transcript) {
              const result: TranscriptionResult = {
                text: transcript,
                isFinal,
                confidence,
                timestamp: Date.now(),
              }

              console.log(`[Deepgram] ${isFinal ? 'Final' : 'Interim'}:`, transcript)
              this.options.onResult?.(result)
            }
          }
        } catch (err) {
          console.error('[Deepgram] Parse error:', err)
        }
      }

      this.ws.onerror = (error) => {
        console.error('[Deepgram] WebSocket error:', error)
        this.options.onError?.('Deepgram connection error')
      }

      this.ws.onclose = () => {
        console.log('[Deepgram] Disconnected')
        this.isRunning = false
        this.options.onEnd?.()
      }

      return true
    } catch (err) {
      console.error('[Deepgram] Failed to start:', err)
      this.options.onError?.('Failed to start Deepgram transcription')
      return false
    }
  }

  /**
   * Start recording from MediaStream and send to Deepgram
   */
  private startRecording(stream: MediaStream): void {
    // Create an AudioContext to resample to 16kHz mono
    const audioContext = new AudioContext({ sampleRate: 16000 })
    const source = audioContext.createMediaStreamSource(stream)

    // Create a script processor for raw PCM data
    const processor = audioContext.createScriptProcessor(4096, 1, 1)

    processor.onaudioprocess = (event) => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return

      const inputData = event.inputBuffer.getChannelData(0)

      // Convert float32 to int16
      const int16Data = new Int16Array(inputData.length)
      for (let i = 0; i < inputData.length; i++) {
        const s = Math.max(-1, Math.min(1, inputData[i]))
        int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7fff
      }

      // Send to Deepgram
      this.ws.send(int16Data.buffer)
    }

    source.connect(processor)
    processor.connect(audioContext.destination)
  }

  /**
   * Stop Deepgram transcription
   */
  stop(): void {
    this.isRunning = false

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop()
    }
    this.mediaRecorder = null

    if (this.ws) {
      this.ws.close()
      this.ws = null
    }

    console.log('[Deepgram] Stopped')
  }

  isActive(): boolean {
    return this.isRunning
  }
}
